import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import SharedPageLayout from './SharedPageLayout';
import { theme } from '../theme';
import historicalPeriodsData from '../data/historicalPeriods.json';
import SectionModal from './SectionModal';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/free-mode';

// Dégradé Outchou pour la frise chronologique
const getTimelineGradient = () => {
  return `linear-gradient(to right, 
    ${theme.colors.outchou_green} 0%, 
    ${theme.colors.outchou_blue} 50%, 
    ${theme.colors.outchou_yellow} 100%
  )`;
};

// Couleur basée sur la position dans le dégradé Outchou
const getPeriodColor = (periodId: number) => {
  const totalPeriods = 11;
  // Inverser la position car l'ordre des périodes est inversé
  const position = 1 - (periodId - 1) / (totalPeriods - 1); // 1 à 0
  
  // Interpolation précise des couleurs Outchou (vert → bleu → jaune)
  if (position <= 0.5) {
    // Transition de green (#44B69D) à blue (#283275)
    const ratio = position * 2; // 0 à 1
    const r = Math.round(68 + ratio * (40 - 68)); // 68 -> 40
    const g = Math.round(182 + ratio * (50 - 182)); // 182 -> 50  
    const b = Math.round(157 + ratio * (117 - 157)); // 157 -> 117
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Transition de blue (#283275) à yellow (#F7E304)
    const ratio = (position - 0.5) * 2; // 0 à 1
    const r = Math.round(40 + ratio * (247 - 40)); // 40 -> 247
    const g = Math.round(50 + ratio * (227 - 50)); // 50 -> 227
    const b = Math.round(117 + ratio * (4 - 117)); // 117 -> 4
    return `rgb(${r}, ${g}, ${b})`;
  }
};

const HistoryContainer = styled.div`
  width: 100%;
  background: ${theme.colors.white};
  min-height: 100vh;
`;

// Suppression du header - on commence directement par la frise

const TimelineContainer = styled.div`
  position: sticky;
  top: 104px; /* Hauteur du header */
  width: 100%;
  background: white;
  z-index: 100;
  padding: 0;
  margin: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);

  .swiper {
    padding: 0;
    margin: 0;
    overflow: visible;
    width: 100%;
  }

  .swiper-wrapper {
    align-items: stretch;
    display: flex;
  }

  .swiper-slide {
    width: auto;
    height: auto;
  }

  @media (max-width: 768px) {
    top: 80px;
  }

  @media (max-width: 480px) {
    top: 70px;
  }
`;

const PeriodItem = styled.div<{ $isActive: boolean; $periodColor: string }>`
  position: relative;
  margin: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 20px;
  border-radius: 0;
  background: ${props => props.$periodColor};
  width: 200px; /* Largeur fixe pour chaque bloc */
  height: 100px;
  min-height: 100px;
  max-height: 100px;
  flex-shrink: 0;
  box-shadow: ${props => props.$isActive ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.1)'};
  transform: ${props => props.$isActive ? 'scale(1.05)' : 'scale(1)'};

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    height: 90px;
    min-height: 90px;
    max-height: 90px;
    width: 180px;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    height: 80px;
    min-height: 80px;
    max-height: 80px;
    width: 160px;
  }
`;

const SwiperSlideStyled = styled(SwiperSlide)`
  width: auto;
  height: auto;
`;

// PeriodMarker supprimé - la ligne passe maintenant au milieu des images


// ActiveIndicator supprimé - remplacé par un effet de couleur d'arrière-plan

// Composants supprimés - remplacés par une structure plus simple

const TimelinePeriodDate = styled.div`
  font-family: ${theme.fonts.title};
  font-weight: 500;
  font-size: 14px;
  color: white;
  margin: 6px 0 0 0;
  text-align: center;
  line-height: 1.1;
  width: 100%;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const TimelinePeriodTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 16px;
  color: white;
  margin: 0;
  text-align: center;
  line-height: 1.1;
  width: 100%;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const PeriodsContainer = styled.div`
  min-height: 100vh;
  position: relative;
  
  /* Ligne verticale qui traverse toutes les époques - positionnée entre date et image */
  &::before {
    content: '';
    position: absolute;
    left: 25%; /* Position entre la date (1fr) et l'image (2fr) */
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(to bottom, 
      ${theme.colors.outchou_green} 0%, 
      ${theme.colors.outchou_blue} 50%, 
      ${theme.colors.outchou_yellow} 100%
    );
    z-index: 1;
    
    /* Écrans entre 1200px et 1600px - timeline très à gauche */
    @media (max-width: 1600px) and (min-width: 1201px) {
      left: 5%; /* Très à gauche pour éviter les images */
    }
    
    @media (max-width: 1200px) {
      left: 15%;
    }
    
    @media (max-width: 1024px) {
      left: 20px;
    }
    
    /* Écrans intermédiaires - utilise le design mobile */
    @media (max-width: 1200px) {
      display: block;
      left: 20px; /* Position à gauche comme en mobile */
      width: 3px; /* Plus fine */
    }
  }

  /* Écrans intermédiaires et plus petits - utilise le design mobile */
  @media (max-width: 1200px) {
    /* Supprime complètement l'espace entre les sections */
    margin: 0;
    padding: 0;
    min-height: 0; /* Supprime complètement la hauteur minimale */
    height: auto; /* Hauteur automatique */
    display: flex; /* Flex pour aligner les sections */
    flex-direction: column; /* Vertical */
    
    /* Timeline verticale à gauche */
    &::before {
      display: block; /* Timeline visible */
      left: 20px; /* Position à gauche */
      transform: none; /* Pas de transformation */
      width: 3px; /* Plus fine */
    }
  }

  @media (max-width: 768px) {
    /* Supprime complètement l'espace entre les sections mobiles */
    margin: 0;
    padding: 0;
    min-height: 0; /* Supprime complètement la hauteur minimale */
    height: auto; /* Hauteur automatique */
    display: flex; /* Flex pour aligner les sections */
    flex-direction: column; /* Vertical */
    
    /* Timeline verticale à gauche sur mobile */
    &::before {
      display: block; /* Remet la timeline en mobile */
      left: 20px; /* Position à gauche */
      transform: none; /* Pas de transformation */
      width: 3px; /* Plus fine sur mobile */
    }
  }
`;

const PeriodSection = styled.div<{ $periodColor: string; $isActive: boolean }>`
  min-height: 80vh; /* Augmenté pour éviter la troncature */
  background: ${props => props.$periodColor.replace('rgb', 'rgba').replace(')', ', 0.2)')}; /* Version pâle mais visible de la couleur */
  padding: 60px 20px; /* Réduit le padding */
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;

  /* Cache le layout desktop sur les écrans intermédiaires */
  @media (max-width: 1200px) {
    display: none;
  }

  /* Marqueur sur la ligne verticale */
  &::after {
    content: '';
    position: absolute;
    left: 25%; /* Même position que la ligne verticale */
    top: 50%;
    width: 20px;
    height: 20px;
    background: ${props => props.$periodColor};
    border: 4px solid white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    
    /* Écrans entre 1200px et 1600px - marqueur très à gauche */
    @media (max-width: 1600px) and (min-width: 1201px) {
      left: 5%; /* Très à gauche pour éviter les images */
    }
    
    @media (max-width: 1200px) {
      left: 15%;
    }
    
    @media (max-width: 1024px) {
      left: 20px;
    }
    
    /* Écrans intermédiaires - utilise le design mobile */
    @media (max-width: 1200px) {
      display: block;
      left: 20px; /* Même position que la timeline */
    }
    
    /* Remet le marqueur en mode mobile */
    @media (max-width: 768px) {
      display: block;
      left: 20px; /* Même position que la timeline */
    }
    
    ${props => props.$isActive && `
      width: 24px;
      height: 24px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    `}
    
    @media (max-width: 1024px) {
      left: 20px;
      transform: translate(-50%, -50%);
    }
  }

  @media (max-width: 768px) {
    display: none; /* Masqué sur mobile */
  }
`;

// Layout mobile pour la superposition - utilise les mêmes styles que le web
const MobilePeriodSection = styled.div<{ $periodColor: string; $isActive: boolean }>`
  background: ${props => props.$periodColor.replace('rgb', 'rgba').replace(')', ', 0.2)')}; /* Version pâle mais visible de la couleur */
  padding: 0;
  display: none; /* Masqué par défaut */
  flex-direction: column;
  position: relative;
  overflow: hidden;

  /* Marqueur sur la ligne verticale pour mobile */
  &::after {
    content: '';
    position: absolute;
    left: 20px; /* Aligné avec la timeline à gauche */
    top: 50%;
    width: 16px;
    height: 16px;
    background: ${props => props.$periodColor};
    border: 3px solid white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    transition: all 0.3s ease;
    
    ${props => props.$isActive && `
      width: 20px;
      height: 20px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    `}
  }

  /* Écrans < 800px - design mobile unifié */
  @media (max-width: 800px) {
    display: flex; /* Affiché sur écrans moyens */
    height: 60vh; /* Hauteur complète pour remplir les sections */
    width: 100vw;
    margin: 0;
    padding: 0;
    position: relative;
  }

  /* Écrans 800px-1200px - design intermédiaire */
  @media (max-width: 1200px) and (min-width: 801px) {
    display: flex; /* Affiché sur écrans intermédiaires */
    height: 100vh; /* Hauteur complète pour remplir les sections */
    width: 100vw; /* Largeur exacte de l'écran */
    margin: 0; /* Supprime les marges */
    padding: 0; /* Supprime les paddings */
    position: relative; /* Position relative pour le contenu */
  }
`;

const MobilePeriodOverlay = styled.div<{ $periodColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    ${props => props.$periodColor}20 0%,
    ${props => props.$periodColor}05 50%,
    transparent 100%
  );
  z-index: 1;
`;

const MobilePeriodContent = styled.div`
  position: relative;
  z-index: 2;
  height: 40vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 10px;
  gap: 5px;
  box-sizing: border-box; /* Inclut padding dans la hauteur */
  overflow: hidden; /* Empêche le débordement */
  margin: 0; /* Supprime les marges */

  /* Écrans < 800px - design mobile unifié */
  @media (max-width: 800px) {
    height: 100vh; /* Hauteur complète pour remplir les sections */
    padding: 5px;
    gap: 5px;
  }

  /* Écrans 800px-1200px - design intermédiaire */
  @media (max-width: 1200px) and (min-width: 801px) {
    height: 100vh; /* Hauteur complète pour remplir les sections */
    padding: 5px; /* Même padding que < 800px */
    gap: 5px;     /* Même gap que < 800px */
  }
`;

// Utilise exactement les mêmes styles que le web
const MobilePeriodTitle = styled.h2<{ $periodColor: string }>`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 36px;
  color: ${props => props.$periodColor};
  margin: 0;
  line-height: 1.2;
  text-align: center;
  z-index: 3;
  max-width: 300px; /* Élargi pour permettre 2 lignes sans troncature */

  /* Écrans 769px-1200px - même comportement que < 800px */
  @media (max-width: 1200px) and (min-width: 769px) {
    max-width: 500px; /* Même largeur que < 800px */
    font-size: 32px; /* Même taille que < 800px */
  }

<<<<<<< HEAD
  /* Écrans 800px-1200px - taille normale */
  @media (max-width: 1200px) and (min-width: 801px) {
    max-width: 500px; /* Taille normale pour 800px-1200px */
    font-size: 32px; /* Taille normale pour 800px-1200px */
  }

  @media (max-width: 768px) {
    font-size: 36px; /* Augmenté pour plus de lisibilité */
    max-width: 500px; /* Réduit pour être moins large */
    margin: 5px 0 15px 0; /* Marge réduite en haut, plus en bas */
=======
  @media (max-width: 800px) {
    font-size: 32px;
    max-width: 500px; /* Élargi pour éviter les retours à la ligne */
>>>>>>> 2cf1b647bbd11dc5ac9c00c3f28d33a8a983f059
  }

  @media (max-width: 768px) {
    font-size: 24px; /* Réduit pour éviter la troncature */
    max-width: 300px; /* Réduit pour éviter la troncature */
  }

  @media (max-width: 480px) {
    font-size: 20px; /* Réduit pour éviter la troncature */
    max-width: 250px; /* Réduit pour éviter la troncature */
  }
  
  @media (max-width: 400px) {
    font-size: 18px; /* Encore plus petit pour les très petits écrans */
    max-width: 200px; /* Encore plus étroit pour les très petits écrans */
  }
`;

const MobilePeriodDate = styled.div<{ $periodColor: string }>`
  font-family: ${theme.fonts.title};
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.$periodColor};
  margin: 0;
  text-align: center;
  z-index: 3;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
  
  @media (max-width: 400px) {
    font-size: 16px; /* Encore plus petit pour les très petits écrans */
  }
`;

const MobilePeriodImage = styled.div`
  width: 400px;
  height: 250px;
  border-radius: 20px;
  overflow: hidden;
  margin: 0;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  z-index: 3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Écrans 769px-1200px - même comportement que < 800px */
  @media (max-width: 1200px) and (min-width: 769px) {
    width: 700px; /* Même largeur que < 800px */
    height: 500px; /* Même hauteur que < 800px */
    flex-shrink: 0; /* Empêche la réduction */
  }

  @media (max-width: 800px) {
    width: 700px;
    height: 500px;
  }

  @media (max-width: 768px) {
    width: 300px; /* Réduit pour être moins large */
    height: 300px; /* Augmenté pour être plus haute */
    margin: 10px 0; /* Marge verticale pour espacer */
  }

  @media (max-width: 480px) {
    width: 250px; /* Réduit pour être moins large */
    height: 250px; /* Augmenté pour être plus haute */
    margin: 8px 0; /* Marge verticale pour espacer */
  }
  
  @media (max-width: 400px) {
    width: 280px; /* Réduit pour les très petits écrans */
    height: 180px; /* Réduit pour les très petits écrans */
  }
`;

const MobilePeriodDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 18px;
  line-height: 1.6;
  color: #333;
  margin: 0;
  max-width: 350px; /* Plus large pour s'étaler sur moins de lignes */
  text-align: justify; /* Texte justifié */
  z-index: 3;

  /* Écrans 769px-1200px - même comportement que < 800px */
  @media (max-width: 1200px) and (min-width: 769px) {
    max-width: 550px; /* Même largeur que < 800px */
    font-size: 18px; /* Même taille que < 800px */
  }

<<<<<<< HEAD
  /* Écrans 800px-1200px - taille normale */
  @media (max-width: 1200px) and (min-width: 801px) {
    max-width: 550px; /* Taille normale pour 800px-1200px */
    font-size: 18px; /* Taille normale pour 800px-1200px */
  }

  @media (max-width: 768px) {
    font-size: 20px; /* Augmenté pour plus de lisibilité */
    max-width: 600px; /* Élargi pour éviter les retours à la ligne */
    margin: 10px 0; /* Marge verticale pour espacer */
=======
  @media (max-width: 800px) {
    font-size: 18px;
    max-width: 550px; /* Élargi pour éviter les retours à la ligne */
>>>>>>> 2cf1b647bbd11dc5ac9c00c3f28d33a8a983f059
  }

  @media (max-width: 768px) {
    font-size: 14px; /* Réduit pour éviter la troncature */
    max-width: 300px; /* Réduit pour éviter la troncature */
  }

  @media (max-width: 480px) {
    font-size: 12px; /* Réduit pour éviter la troncature */
    max-width: 250px; /* Réduit pour éviter la troncature */
  }
  
  @media (max-width: 400px) {
    font-size: 11px; /* Encore plus petit pour les très petits écrans */
    max-width: 200px; /* Encore plus étroit pour les très petits écrans */
  }
`;

const MobileLearnMoreButton = styled.button<{ $periodColor: string }>`
  background: ${props => props.$periodColor};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
  width: fit-content;
  display: inline-block;
  z-index: 3;

  &:hover {
    background: ${props => props.$periodColor}dd;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const PeriodContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 0.8fr 1.5fr 1.7fr; /* Réduit la colonne date, augmente le texte */
  gap: 60px; /* Plus d'espace entre les colonnes */
  align-items: start; /* Alignement en haut pour que le texte soit aligné avec l'image */

  /* Écrans entre 1200px et 1600px - aligne les images et dates */
  @media (max-width: 1600px) and (min-width: 1201px) {
    align-items: center; /* Centre les images verticalement */
    grid-template-columns: 0.5fr 1fr 0.5fr; /* Colonnes égales pour alignement */
    gap: 40px; /* Réduit l'espace entre les colonnes */
  }

  /* Écrans intermédiaires - utilise le même design que mobile */
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 0px;
    text-align: center;
    align-items: center;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 0px;
    text-align: center;
    align-items: center;
  }

  @media (max-width: 768px) {
    gap: 0px;
  }
`;

const PeriodDateColumn = styled.div<{ $periodColor: string }>`
  text-align: left;
  display: flex;
  align-items: center; /* Centre verticalement la date */
  height: 100%;
  justify-content: flex-start;
  padding-left: 0px;
  margin-left: -100px;

  /* Écrans entre 1200px et 1600px - dates dans une box de largeur fixe */
  @media (max-width: 1600px) and (min-width: 1201px) {
    margin-left: 0px; /* Remet à zéro pour l'alignement */
    justify-content: center; /* Centre dans la colonne */
    text-align: center; /* Centre le texte */
    width: 100%; /* Prend toute la largeur de la colonne */
    max-width: 200px; /* Largeur fixe pour la box */
    margin: 0 auto; /* Centre la box */
  }

  /* Écrans intermédiaires - utilise le design mobile */
  @media (max-width: 1200px) {
    margin-left: 0px;
    justify-content: center;
    text-align: center;
    position: relative;
    z-index: 3; /* Au-dessus du point de repère */
  }

  @media (max-width: 1024px) {
    text-align: left;
    justify-content: flex-start;
    padding-left: 0px;
    margin-left: -60px;
  }
`;

const PeriodDate = styled.div<{ $periodColor: string }>`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 24px;
  color: ${props => props.$periodColor};
  margin-bottom: 10px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Écrans entre 1200px et 1600px - dates dans box fixe avec retour à la ligne */
  @media (max-width: 1600px) and (min-width: 1201px) {
    white-space: normal; /* Permet le retour à la ligne */
    line-height: 1.2; /* Espacement entre les lignes */
    max-width: 180px; /* Largeur fixe pour la box */
    text-align: center; /* Centre le texte */
    word-wrap: break-word; /* Permet la coupure des mots longs */
  }

  /* Écrans intermédiaires - permet le passage à 2 lignes */
  @media (max-width: 1100px) {
    white-space: normal; /* Permet le retour à la ligne */
    line-height: 1.2; /* Espacement entre les lignes */
    max-width: 200px; /* Largeur maximale pour contrôler le wrapping */
    text-align: center; /* Centre le texte */
  }

  @media (max-width: 768px) {
    font-size: 20px;
    white-space: nowrap; /* Remet sur une ligne en mobile */
    max-width: none; /* Supprime la largeur max en mobile */
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const PeriodImageColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PeriodImage = styled.div`
  width: 100%;
  max-width: 500px;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 300px;
  }

  @media (max-width: 480px) {
    height: 250px;
  }
`;

const PeriodInfoColumn = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: center; /* Centre verticalement le contenu */
  height: 100%;

  @media (max-width: 1024px) {
    text-align: center;
    align-items: center;
  }
`;

const PeriodTitle = styled.h2<{ $periodColor: string }>`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 36px;
  color: ${props => props.$periodColor};
  margin: 0 0 20px 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const PeriodDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 18px;
  line-height: 1.6;
  color: #333;
  margin: 0 0 30px 0;
  text-align: justify; /* Texte justifié */

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const LearnMoreButton = styled.button<{ $periodColor: string }>`
  background: ${props => props.$periodColor};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  width: fit-content;
  display: inline-block;

  &:hover {
    background: ${props => props.$periodColor}dd;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

// Nouveaux composants pour la structure "En savoir plus"
const ColorBand = styled.div<{ $periodColor: string }>`
  background: ${props => props.$periodColor};
  padding: 20px;
  text-align: center;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const ModalPeriodTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: white;
`;

const ModalPeriodDate = styled.p`
  font-family: ${theme.fonts.title};
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: white;
  opacity: 0.9;
`;

const CenteredImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  margin: 0 auto;
  width: fit-content;
  
  /* Sur web, s'adapte parfaitement à l'image */
  @media (min-width: 769px) {
    width: fit-content;
    max-width: none;
  }
`;

const ModalPeriodImage = styled.img`
  width: auto;
  max-width: 100%;
  max-height: 400px;
  height: auto;
  object-fit: contain;
  border-radius: 0;
  box-shadow: none;
  display: block;
  
  /* Sur web, l'image détermine la largeur du modal */
  @media (min-width: 769px) {
    width: auto;
    max-width: none;
    max-height: 500px;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 300px;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    max-height: 250px;
  }
`;

const SectionsContainer = styled.div`
  padding: 10px;
  background: white;
  margin: 0;
  width: 100%;
  min-width: fit-content;
`;

const SectionItem = styled.div<{ periodColor: string }>`
  border: none;
  border-radius: 0;
    margin-bottom: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  border-bottom: 1px solid ${props => props.periodColor};
  
  &:hover {
    box-shadow: none;
  }
`;

const SectionHeader = styled.button<{ isExpanded: boolean; periodColor: string }>`
  width: 100%;
  padding: 10px 15px;
  background: ${props => props.isExpanded ? '#f8f9fa' : 'white'};
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  font-family: ${theme.fonts.title};
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.periodColor};

  &:hover {
    background: #f8f9fa;
  }
`;

const SectionContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: white;
`;

const SectionText = styled.div`
  padding: 10px 15px;
  line-height: 1.6;
  color: #555;
    font-size: 14px;
`;

const ExpandIcon = styled.span<{ isExpanded: boolean; periodColor: string }>`
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
  font-size: 18px;
  color: ${props => props.periodColor};
`;

// Modal styles supprimés - utilisation du composant SectionModal commun

// Historical periods data from JSON file
const historicalPeriods = historicalPeriodsData.periods;


const HistoirePage: React.FC = () => {
  const [visiblePeriod, setVisiblePeriod] = useState(historicalPeriods[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPeriod, setModalPeriod] = useState<typeof historicalPeriods[0] | null>(null);
  const [modalWidth, setModalWidth] = useState<number | null>(null);
  const periodRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timelineSwiperRef = useRef<SwiperType | null>(null);
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const isSwipingRef = useRef<boolean>(false);

  // Fonction pour obtenir l'image correspondante à chaque période
  const getPeriodImage = (periodId: number) => {
    const imageMap: { [key: number]: string } = {
      1: '/shared_assets/images/histoire/era-1.jpg',
      2: '/shared_assets/images/histoire/era-2.jpeg',
      3: '/shared_assets/images/histoire/era-3.jpeg',
      4: '/shared_assets/images/histoire/era-4.jpeg',
      5: '/shared_assets/images/histoire/era-5.jpeg',
      6: '/shared_assets/images/histoire/era-6.jpeg',
      7: '/shared_assets/images/histoire/era-7.jpeg',
      8: '/shared_assets/images/histoire/era-8.jpg',
      9: '/shared_assets/images/histoire/era-9.jpeg',
      10: '/shared_assets/images/histoire/era-10.jpeg',
      11: '/shared_assets/images/histoire/era-11.jpeg'
    };
    return imageMap[periodId] || '/shared_assets/images/histoire/era-1.jpg';
  };


  // Flag pour désactiver temporairement l'IntersectionObserver pendant un scroll manuel
  const isManualScrollRef = useRef(false);

  // Intersection Observer pour détecter quelle période est visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Ignorer les mises à jour si c'est un scroll manuel
        if (isManualScrollRef.current) {
          console.log('[HistoirePage] IntersectionObserver ignoré car scroll manuel en cours');
          return;
        }
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const periodId = parseInt(entry.target.getAttribute('data-period-id') || '1');
            const period = historicalPeriods.find(p => p.id === periodId);
            if (period) {
              console.log('[HistoirePage] IntersectionObserver détecte période visible:', period.title);
              setVisiblePeriod(period);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '-100px 0px -50% 0px' // Prendre en compte la frise fixe
      }
    );

    periodRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleLearnMore = useCallback((period: typeof historicalPeriods[0]) => {
    setModalPeriod(period);
    setIsModalOpen(true);
    
    // Calculer la largeur de l'image
    const img = new Image();
    img.onload = () => {
      const maxHeight = 500;
      const aspectRatio = img.width / img.height;
      const calculatedWidth = Math.min(img.width, maxHeight * aspectRatio);
      setModalWidth(calculatedWidth + 40); // +40 pour le padding et les bordures
    };
    img.src = getPeriodImage(period.id);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalPeriod(null);
  }, []);

  return (
    <>
      <SharedPageLayout
        backgroundImage="/shared_assets/images/navigation/histoire.jpeg"
        heroType="search"
        heroTitle="Histoire"
        heroSubtitle="Derrière chaque geste, chaque plat et chaque mot, il y a une histoire.
L’Algérie, c’est des siècles et des cultures qui s’entremêlent.
Parcourez cette frise pour voir comment tout s’est tissé."
      >
      <HistoryContainer>
        {/* Frise chronologique fixe en haut */}
        <TimelineContainer>
          <Swiper
            modules={[FreeMode]}
            freeMode={true}
            slidesPerView="auto"
            spaceBetween={0}
            preventClicks={false}
            preventClicksPropagation={false}
            onTouchStart={(swiper: SwiperType, event: TouchEvent) => {
              const touch = event.touches?.[0];
              if (touch) {
                touchStartXRef.current = touch.clientX;
                touchStartYRef.current = touch.clientY;
                isSwipingRef.current = false;
                console.log('[HistoirePage] TouchStart - X:', touch.clientX, 'Y:', touch.clientY);
              }
            }}
            onTouchMove={(swiper: SwiperType, event: TouchEvent) => {
              const touch = event.touches?.[0];
              if (touch && touchStartXRef.current !== 0) {
                const deltaX = Math.abs(touch.clientX - touchStartXRef.current);
                const deltaY = Math.abs(touch.clientY - touchStartYRef.current);
                // Si mouvement horizontal significatif ET > vertical, c'est un swipe
                if (deltaX > 15 && deltaX > deltaY * 1.5) {
                  isSwipingRef.current = true;
                  console.log('[HistoirePage] TouchMove - Swipe détecté, deltaX:', deltaX, 'deltaY:', deltaY);
                }
              }
            }}
            onTouchEnd={(swiper: SwiperType, event: TouchEvent) => {
              // Vérifier la distance finale pour confirmer le swipe
              const touch = event.changedTouches?.[0];
              if (touch && touchStartXRef.current !== 0) {
                const deltaX = Math.abs(touch.clientX - touchStartXRef.current);
                const deltaY = Math.abs(touch.clientY - touchStartYRef.current);
                // Si mouvement horizontal significatif, confirmer le swipe
                if (deltaX > 15 && deltaX > deltaY * 1.5) {
                  isSwipingRef.current = true;
                  console.log('[HistoirePage] TouchEnd - Swipe confirmé, deltaX:', deltaX, 'deltaY:', deltaY);
                }
              }
              
              console.log('[HistoirePage] TouchEnd - isSwiping:', isSwipingRef.current);
              // Réinitialiser après un délai plus long pour laisser le temps aux handlers de vérifier
              setTimeout(() => {
                touchStartXRef.current = 0;
                touchStartYRef.current = 0;
                isSwipingRef.current = false;
              }, 300);
            }}
            onSlideChange={(swiper: SwiperType) => {
              // Si le slide change, c'est qu'un swipe a eu lieu
              isSwipingRef.current = true;
              console.log('[HistoirePage] SlideChange - Swipe confirmé');
            }}
            onSwiper={(swiper: SwiperType) => {
              timelineSwiperRef.current = swiper;
            }}
            style={{ width: '100%', padding: '0' }}
          >
            {historicalPeriods.slice().reverse().map((period) => {
              const periodColor = getPeriodColor(period.id);
              const isActive = visiblePeriod.id === period.id;
              
              const handleClick = () => {
                console.log('[HistoirePage] handleClick appelé pour période:', period.title, 'isSwiping:', isSwipingRef.current);
                // Ne pas déclencher le clic si c'était un swipe
                if (isSwipingRef.current) {
                  console.log('[HistoirePage] Clic ignoré car c\'était un swipe');
                  return;
                }
                console.log('[HistoirePage] Clic valide, changement de période vers:', period.title);
                
                // Activer le flag de scroll manuel
                isManualScrollRef.current = true;
                setVisiblePeriod(period);
                console.log('[HistoirePage] visiblePeriod mis à jour vers:', period.title);
                
                // Scroll vers la section correspondante - utiliser la ref directement
                const targetElement = periodRefs.current[period.id - 1];
                if (targetElement) {
                  // Attendre que le DOM soit prêt
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      const currentScrollTop = window.pageYOffset || 
                                               document.documentElement.scrollTop || 
                                               document.body.scrollTop || 0;
                      
                      console.log('[HistoirePage] Scroll vers élément trouvé, position avant scroll:', currentScrollTop);
                      
                      // Calculer la position de scroll
                      const rect = targetElement.getBoundingClientRect();
                      const elementTop = rect.top + currentScrollTop;
                      const elementHeight = targetElement.offsetHeight;
                      const windowHeight = window.innerHeight;
                      const timelineHeight = 104;
                      
                      const scrollPosition = elementTop - (windowHeight / 2) + (elementHeight / 2) - timelineHeight;
                      const scrollTop = Math.max(0, scrollPosition);
                      
                      console.log('[HistoirePage] Position cible calculée:', scrollTop);
                      
                      // Utiliser une animation de scroll manuelle avec requestAnimationFrame
                      // Cela force le scroll même si scrollTo ne fonctionne pas
                      const startScrollTop = currentScrollTop;
                      const distance = scrollTop - startScrollTop;
                      const duration = 800;
                      let startTime: number | null = null;
                      
                      const animateScroll = (currentTime: number) => {
                        if (startTime === null) startTime = currentTime;
                        const timeElapsed = currentTime - startTime;
                        const progress = Math.min(timeElapsed / duration, 1);
                        
                        // Easing function (ease-in-out)
                        const ease = progress < 0.5 
                          ? 2 * progress * progress 
                          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                        
                        const currentScroll = startScrollTop + distance * ease;
                        
                        // Appliquer le scroll avec toutes les méthodes possibles
                        window.scrollTo(0, currentScroll);
                        document.documentElement.scrollTop = currentScroll;
                        document.body.scrollTop = currentScroll;
                        
                        // Vérifier périodiquement si le scroll fonctionne
                        const actualScroll = window.pageYOffset || 
                                           document.documentElement.scrollTop || 
                                           document.body.scrollTop || 0;
                        
                        if (progress < 1) {
                          requestAnimationFrame(animateScroll);
                        } else {
                          // Animation terminée
                          const finalScrollY = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
                          console.log('[HistoirePage] Animation terminée, position finale:', finalScrollY);
                          isManualScrollRef.current = false;
                          console.log('[HistoirePage] IntersectionObserver réactivé');
                        }
                      };
                      
                      // Démarrer l'animation
                      requestAnimationFrame(animateScroll);
                    });
                  });
                } else {
                  console.log('[HistoirePage] Élément non trouvé dans periodRefs pour period.id:', period.id);
                  isManualScrollRef.current = false;
                }
              };
              
              return (
                <SwiperSlideStyled key={period.id}>
                  <PeriodItem
                    $isActive={isActive}
                    $periodColor={periodColor}
                    onClick={(e) => {
                      console.log('[HistoirePage] onClick sur PeriodItem - période:', period.title);
                      handleClick();
                    }}
                    onTouchEnd={(e) => {
                      console.log('[HistoirePage] onTouchEnd sur PeriodItem - période:', period.title, 'isSwiping:', isSwipingRef.current);
                      // Sur mobile, utiliser onTouchEnd pour les clics
                      if (!isSwipingRef.current) {
                        console.log('[HistoirePage] onTouchEnd - appel de handleClick');
                        handleClick();
                      } else {
                        console.log('[HistoirePage] onTouchEnd - clic ignoré car swipe');
                      }
                    }}
                  >
                    <TimelinePeriodTitle>{period.title}</TimelinePeriodTitle>
                    <TimelinePeriodDate>
                      {period.date}
                    </TimelinePeriodDate>
                  </PeriodItem>
                </SwiperSlideStyled>
              );
            })}
          </Swiper>
        </TimelineContainer>

        {/* Sections des périodes */}
        <PeriodsContainer>
          {historicalPeriods.slice().reverse().map((period) => {
            const periodColor = getPeriodColor(period.id);
            const isActive = visiblePeriod.id === period.id;
            
            return (
              <div 
                key={period.id}
                ref={(el) => { periodRefs.current[period.id - 1] = el; }}
                data-period-id={period.id}
                style={{ position: 'relative' }}
              >
                {/* Layout desktop/tablet */}
              <PeriodSection
                $periodColor={periodColor}
                $isActive={isActive}
                data-period-id={period.id}
              >
                <PeriodContent>
                  <PeriodDateColumn $periodColor={periodColor}>
                    <PeriodDate $periodColor={periodColor}>
                      {period.date}
                    </PeriodDate>
                  </PeriodDateColumn>

                  <PeriodImageColumn>
                      <PeriodImage 
                        onClick={() => handleLearnMore(period)}
                        style={{ cursor: 'pointer' }}
                      >
                      <img 
                        src={getPeriodImage(period.id)} 
                        alt={`${period.title} - ${period.date}`}
                          title={period.imageCaption}
                      />
                    </PeriodImage>
                  </PeriodImageColumn>

                  <PeriodInfoColumn>
                    <PeriodTitle $periodColor={periodColor}>
                      {period.title}
                    </PeriodTitle>
                    <PeriodDescription>
                      {period.shortDescription}
                    </PeriodDescription>
                      <LearnMoreButton 
                        $periodColor={periodColor}
                        onClick={() => handleLearnMore(period)}
                    >
                      En savoir plus
                      </LearnMoreButton>
                  </PeriodInfoColumn>
                </PeriodContent>
              </PeriodSection>

                {/* Layout mobile - superposition */}
                <MobilePeriodSection
                  ref={(el) => { periodRefs.current[period.id - 1] = el; }}
                  $periodColor={periodColor}
                  $isActive={isActive}
                  data-period-id={period.id}
                >
                  <MobilePeriodOverlay $periodColor={periodColor} />
                  <MobilePeriodContent>
                    <MobilePeriodTitle $periodColor={periodColor}>
                      {period.title}
                    </MobilePeriodTitle>
                    <MobilePeriodDate $periodColor={periodColor}>
                      {period.date}
                    </MobilePeriodDate>
                    <MobilePeriodImage 
                      onClick={() => handleLearnMore(period)}
          style={{
                        cursor: 'pointer'
                      }}
                    >
                      <img 
                        src={getPeriodImage(period.id)} 
                        alt={`${period.title} - ${period.date}`}
            style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </MobilePeriodImage>
                    <MobilePeriodDescription>
                      {period.shortDescription}
                    </MobilePeriodDescription>
                    <MobileLearnMoreButton 
                      $periodColor={periodColor}
                      onClick={() => handleLearnMore(period)}
                    >
                      En savoir plus
                    </MobileLearnMoreButton>
                  </MobilePeriodContent>
                </MobilePeriodSection>
              </div>
            );
          })}
        </PeriodsContainer>

      </HistoryContainer>
    </SharedPageLayout>

    {/* Modal optimisé avec SectionModal commun */}
    {isModalOpen && modalPeriod && modalPeriod.sections && (
      <SectionModal
        title={modalPeriod.title}
        subtitle={modalPeriod.date}
        imageUrl={getPeriodImage(modalPeriod.id)}
        imageCaption={modalPeriod.imageCaption}
        sections={Object.fromEntries(
          Object.entries(modalPeriod.sections).filter(([_, value]) => value !== undefined && value !== null)
        ) as { [key: string]: string | string[] }}
        color={getPeriodColor(modalPeriod.id)}
        isOpen={isModalOpen}
        onClose={closeModal}
        modalWidth={modalWidth}
      />
    )}
    </>
  );
};

export default HistoirePage; 